import { DateTime } from 'luxon';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { DateHelper } from '@getaccept/lib-shared/src/helpers/date.helper';
import type { EmojiLocalization } from '@getaccept/lib-ui';
import type { ChatParticipant, Message } from '../types';

export class ChatHelper {
  static getEmojiLocalization(): EmojiLocalization {
    return {
      choose: t('choose').toString(),
      search: t('find-your-emoji').toString(),
      notfound: t('no-emoji-found').toString(),
      searchresult: t('search-results').toString(),
      recent: t('frequently-used').toString(),
      smileys: t('smileys-and-emoticon').toString(),
      people: t('people-and-body').toString(),
      nature: t('animals-and-nature').toString(),
      foods: t('food-and-drink').toString(),
      activity: t('activity').toString(),
      places: t('travel-and-places').toString(),
      objects: t('objects').toString(),
      symbols: t('symbols').toString(),
      flags: t('flags').toString(),
      custom: t('custom').toString(),
    };
  }

  static getPreviousMessage(message: Message, messages: Message[]): Message {
    const index: number = messages.indexOf(message) - 1;
    return messages[index];
  }

  static getNextMessage(message: Message, messages: Message[]): Message {
    const index: number = messages.indexOf(message) + 1;
    return messages[index];
  }

  static getLastMessage(messages: Message[]) {
    return messages[messages.length - 1];
  }

  static getFullname(message: Message, user: User, recipients: Recipient[]): string {
    if (!user && !recipients) {
      return '';
    }
    if (message.owner.userId) {
      return user.name || user.fullName || `${user.firstName} ${user.lastName}`;
    }
    const recipient: Recipient = this.getRecipient(message, recipients);
    if (!recipient) {
      return '';
    }
    return (
      recipient.fullName || `${recipient.firstName} ${recipient.lastName}`.trim() || recipient.email
    );
  }

  static getThumbUrl(message: Message, user: User, recipients: Recipient[]): string {
    if (message.owner.userId) {
      return user.thumbUrl;
    }
    const recipient: Recipient = this.getRecipient(message, recipients);
    return recipient?.thumbUrl;
  }

  static isByActiveRecipient(message: Message, recipientId: string): boolean {
    return message.owner.recipientId === recipientId;
  }

  private static getRecipient(message: Message, recipients: Recipient[]): Recipient {
    return recipients.find(recipient => recipient.id === message.owner.recipientId);
  }

  static isLastMessageInBlock(
    message: Message,
    lastMessageId: string,
    nextMessage: Message
  ): boolean {
    if (message.id === lastMessageId) {
      return true;
    }
    if (nextMessage?.metadata?.repliedTo) {
      return true;
    }
    return !this.isSameParticipant(nextMessage.owner, message.owner);
  }

  private static isFirstMessageInMonologue(previousMessage: Message, message: Message): boolean {
    if (!previousMessage) {
      return true;
    }
    return !this.isSameParticipant(previousMessage.owner, message.owner);
  }

  static isFirstMessageInBlock(previousMessage: Message, message: Message): boolean {
    if (message.metadata?.repliedTo) {
      return false;
    }
    return (
      this.isFirstMessageInMonologue(previousMessage, message) ||
      this.showTimestamp(previousMessage, message.createdAt)
    );
  }

  static getReplyMessage(message: Message, messages: Message[]): Message {
    if (!message.metadata?.repliedTo) {
      return undefined;
    }
    return messages.find(({ id }: Message) => id === message.metadata.repliedTo);
  }

  static showTimestamp(previousMessage: Message, date: number): boolean {
    if (!previousMessage) {
      return true;
    }
    return !DateHelper.isSameHour(
      DateTime.fromMillis(date),
      DateTime.fromMillis(previousMessage.createdAt)
    );
  }

  static date(date: number): string {
    return DateHelper.formattedDate(DateTime.fromMillis(date));
  }

  static time(date: number) {
    return DateTime.fromMillis(date).toLocaleString({ ...DateTime.TIME_SIMPLE });
  }

  static fullTimestamp(date: number) {
    return `${this.date(date)} ${t('at')} ${this.time(date)}`;
  }

  static isSameParticipant(
    firstParticipant: ChatParticipant,
    secondParticipant: ChatParticipant
  ): boolean {
    if (
      firstParticipant.recipientId &&
      secondParticipant.recipientId &&
      firstParticipant.recipientId === secondParticipant.recipientId
    ) {
      return true;
    }
    return (
      firstParticipant.userId &&
      secondParticipant.userId &&
      firstParticipant.userId === secondParticipant.userId
    );
  }

  static getRecipientFromId = (recipients: Recipient[], participant: ChatParticipant): Recipient =>
    recipients.find(({ id }) => id === participant.recipientId || id === participant.userId);

  static getRecipientsFromIds = (
    recipients: Recipient[],
    participants: ChatParticipant[]
  ): Recipient[] => {
    const recipientIds = participants.map(
      participant => participant.recipientId || participant.userId
    );
    return recipients.filter(({ id }) => recipientIds.includes(id));
  };

  static getParticipantNames = (recipients: Recipient[], participants: ChatParticipant[]) => {
    const chatRecipients = ChatHelper.getRecipientsFromIds(recipients, participants);
    const names = chatRecipients.map(({ fullName }) => fullName);
    return names.join(', ');
  };

  static getMessageState(
    message: Message,
    user: User,
    recipients: Recipient[],
    messages: Message[],
    owner: ChatParticipant
  ) {
    const name = ChatHelper.getFullname(message, user, recipients);
    const thumbUrl = ChatHelper.getThumbUrl(message, user, recipients);
    const date = ChatHelper.date(message.createdAt);
    const time = ChatHelper.time(message.createdAt);
    const fullTimestamp = ChatHelper.fullTimestamp(message.createdAt);
    const isBySender = ChatHelper.isSameParticipant(message.owner, owner);
    const nextMessage = ChatHelper.getNextMessage(message, messages);
    const previousMessage = ChatHelper.getPreviousMessage(message, messages);
    const lastMessage = ChatHelper.getLastMessage(messages);
    const isFirstMessageInBlock = ChatHelper.isFirstMessageInBlock(previousMessage, message);
    const showTimestamp = ChatHelper.showTimestamp(previousMessage, message.createdAt);
    const isLastMessageInBlock = ChatHelper.isLastMessageInBlock(
      message,
      lastMessage.id,
      nextMessage
    );
    const replyMessage = ChatHelper.getReplyMessage(message, messages);

    return {
      name,
      thumbUrl,
      date,
      time,
      fullTimestamp,
      isBySender,
      showTimestamp,
      isFirstMessageInBlock,
      isLastMessageInBlock,
      replyMessage,
    };
  }
}
